import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Gets the effective minimum value for cart quantity selector
   * Cart page: 0, so the minus button can drive the quantity down to remove
   * the line item (the cart's quantity-update listener treats 0 as a removal)
   * @returns {number} The effective min
   */
  getEffectiveMin() {
    return 0;
  }

  /**
   * Updates the cart quantity by a step.
   * Decreasing below the variant's order minimum removes the line item instead
   * of landing on an invalid partial quantity (the cart's quantity-update
   * listener treats a quantity of 0 as a removal).
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const candidate = value + step * stepMultiplier;
    const newValue = candidate < min ? 0 : Math.min(effectiveMax ?? Infinity, candidate);

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const effectiveMin = this.getEffectiveMin();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= effectiveMin;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
